// Lifecycle presentation config for the property list + detail views.
//
// seller-service returns a coarse `stage` (collapsed from the 11-state property
// machine in admin-service) plus `stageStep`; the FE only maps those to
// labels/colors/copy here. i18n is not wired yet, so copy lives as plain strings
// (per repo convention).
//
// Every property transition is admin-driven, so unlike the listing-request
// lifecycle there is no "action required" stage and no seller-action guards.
import type { ChipColor } from '@uiKit/elements/Chip.vue'
import type { PiIconName } from '@uiKit/elements/PiIcon.vue'

// The coarse tracker stages emitted by the backend (PropertyStage).
//
// `listed` used to be one stage covering everything from admin approval onwards,
// and it rendered as a green "Live" chip. It is two stages, because it is two
// things: `listing_setup` is pricing, token supply and the regulatory submission,
// and `live` is the only one of them an investor can act on.
export type PropertyStage =
  | 'onboarding'
  | 'in_review'
  | 'registration'
  | 'listing_setup'
  | 'live'
  | 'funded'
  | 'raise_failed'
  | 'rejected'
  | 'archived'

// How the current stage is going (PropertyStageState). Orthogonal to the stage,
// the way the listing-request module already treats its own: a property sent back
// for corrections has stalled on its stage, it has not moved to a new one.
export type PropertyStageState = 'in_progress' | 'attention' | 'rejected' | 'cancelled'

// Typed stage values — the single source of truth for comparing `stage`, so a
// backend rename breaks the type-check rather than silently breaking a count.
export const PROPERTY_STAGE = {
  onboarding: 'onboarding',
  inReview: 'in_review',
  registration: 'registration',
  listingSetup: 'listing_setup',
  live: 'live',
  funded: 'funded',
  raiseFailed: 'raise_failed',
  rejected: 'rejected',
  archived: 'archived',
} as const satisfies Record<string, PropertyStage>

export const PROPERTY_STAGE_STATE = {
  inProgress: 'in_progress',
  attention: 'attention',
  rejected: 'rejected',
  cancelled: 'cancelled',
} as const satisfies Record<string, PropertyStageState>

export interface StageMeta {
  label: string
  color: ChipColor
}

// Green is reserved for `live`. Setup is amber like registration, because it is
// the same kind of thing to the seller — work in progress they are waiting on.
export const PROPERTY_STAGE_META: Record<PropertyStage, StageMeta> = {
  onboarding: { label: 'Being prepared', color: 'dark' },
  in_review: { label: 'Under review', color: 'primary' },
  registration: { label: 'Registering', color: 'warning' },
  listing_setup: { label: 'Preparing listing', color: 'warning' },
  live: { label: 'Live', color: 'success' },
  funded: { label: 'Fully funded', color: 'success' },
  // Danger, not neutral: the raise ending short is the one outcome on this tracker
  // the seller needs to act on, even if the action is a conversation.
  raise_failed: { label: 'Raise incomplete', color: 'danger' },
  rejected: { label: 'Rejected', color: 'danger' },
  archived: { label: 'Archived', color: 'neutral' },
}

export const PROPERTY_STAGE_META_FALLBACK: StageMeta = { label: 'In Progress', color: 'neutral' }

export function propertyStageMetaOf(stage: string): StageMeta {
  return PROPERTY_STAGE_META[stage as PropertyStage] ?? PROPERTY_STAGE_META_FALLBACK
}

// Past funding. The backend stage stops at `funded`; the property's DLD transfer
// stamp and its settlement carry it on to payout. "Paid out" is the property
// settling in full — the same fact as the list's Settled filter — so the list
// and the detail never disagree.
export type PropertyPayoutPhase = 'none' | 'dldTransferred' | 'paidOut'

export interface PropertyPayoutFacts {
  stage: string
  dldTransferredAt: string | null
  isSettled: boolean
}

export function propertyPayoutPhaseOf(property: PropertyPayoutFacts): PropertyPayoutPhase {
  if (property.stage !== PROPERTY_STAGE.funded) return 'none'
  if (property.isSettled) return 'paidOut'
  if (property.dldTransferredAt) return 'dldTransferred'
  return 'none'
}

const PAYOUT_PHASE_META: Record<Exclude<PropertyPayoutPhase, 'none'>, StageMeta> = {
  dldTransferred: { label: 'Payout in progress', color: 'primary' },
  paidOut: { label: 'Payout settled', color: 'success' },
}

/** The property's status pill — its stage, or past funding, where the payout stands. */
export function propertyStatusMetaOf(property: PropertyPayoutFacts): StageMeta {
  const phase = propertyPayoutPhaseOf(property)
  return phase === 'none' ? propertyStageMetaOf(property.stage) : PAYOUT_PHASE_META[phase]
}

// The 6 canonical tracker steps. `stageStep` from the backend drives
// done/active/upcoming; `stageStepCount` confirms the total so the FE can never
// disagree with the server's classification.
//
// "Listing" and "Live" are separate nodes for the same reason the stages are: the
// seller used to watch step 4 light up green and then wait weeks with nothing
// moving, because the step covered work that had barely started.
export interface PropertyStepMeta {
  step: number
  label: string
  caption: string
}

export const PROPERTY_STEPS: PropertyStepMeta[] = [
  { step: 1, label: 'Prepared', caption: 'Property created' },
  { step: 2, label: 'Review', caption: 'Verification & compliance' },
  { step: 3, label: 'Registration', caption: 'Regulatory asset registration' },
  { step: 4, label: 'Listing', caption: 'Pricing & submission' },
  { step: 5, label: 'Live', caption: 'Open to investors' },
  { step: 6, label: 'Funded', caption: 'Raise complete' },
]

// Step 7 is `settled` and is deliberately absent: settlement does not exist yet,
// and a node no property can reach would be the same kind of lie the split of
// `listed` into `listing_setup` and `live` was made to remove. `stageStepCount`
// comes off the wire, so adding it later needs no change here.

// Tone of the tracker's active step, derived from the stage STATE rather than the
// stage — the same rule the listing-request module follows. A stage never carries
// its own outcome: `rejected` is how a stage ended, not which stage it is.
export type PropertyStageTone = 'normal' | 'negative'

export function propertyStageToneOf(state: string): PropertyStageTone {
  const isStopped =
    state === PROPERTY_STAGE_STATE.rejected || state === PROPERTY_STAGE_STATE.cancelled
  return isStopped ? 'negative' : 'normal'
}

export type LifecycleTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'

// "What happens next" copy for the detail screen. Answers the seller's actual
// question on this page — "what am I waiting for?" — per stage. Every stage here
// is admin-driven, so none of this copy asks the seller to do anything.
export interface StageOutlook {
  headline: string
  detail: string
  icon: PiIconName
  tone: LifecycleTone
}

export const PROPERTY_STAGE_OUTLOOK: Record<PropertyStage, StageOutlook> = {
  onboarding: {
    headline: 'Being prepared',
    detail:
      'Our team is completing your property record — details, documents and financials — before it enters review.',
    icon: 'hourglass',
    tone: 'primary',
  },
  in_review: {
    headline: 'Under review',
    detail:
      'Your property is going through verification and compliance checks. Nothing is needed from you.',
    icon: 'search',
    tone: 'primary',
  },
  // Not "tokenising": nothing is minted here. This stage is the regulated
  // partner registering the asset so it CAN be offered — tokens reach investors
  // only after the raise completes, which is the `funded` stage below.
  registration: {
    headline: 'Registration in progress',
    detail:
      'Approved internally and now being registered with our regulated partner ahead of listing.',
    icon: 'refresh',
    tone: 'warning',
  },
  // Says what is actually happening without naming the vendor. The seller's real
  // question here is "it was approved, so why isn't it live?" — this answers it.
  listing_setup: {
    headline: 'Approved — setting up your listing',
    detail:
      'Pricing, tokens and regulatory submission. You’ll be notified the moment it opens to investors.',
    icon: 'hourglass',
    tone: 'warning',
  },
  live: {
    headline: 'Live on the marketplace',
    detail: 'Open to investors on the Marketplace. Your funding window is shown below.',
    icon: 'verified',
    tone: 'success',
  },
  funded: {
    headline: 'Fully funded',
    detail:
      'The raise is complete. Tokens are being issued to investors and your settlement is being prepared.',
    icon: 'verified',
    tone: 'success',
  },
  // No promise of a date or a next step: relisting is a commercial conversation,
  // not something the tracker can schedule.
  raise_failed: {
    headline: 'The raise did not complete',
    detail:
      'Investors are being refunded. Your relationship manager will be in touch about relisting.',
    icon: 'times-circle',
    tone: 'danger',
  },
  rejected: {
    headline: 'Not approved',
    detail: 'This property did not pass review. Your relationship manager can walk you through it.',
    icon: 'times-circle',
    tone: 'danger',
  },
  archived: {
    headline: 'Archived',
    detail: 'This property is no longer active on the platform.',
    icon: 'ban',
    tone: 'neutral',
  },
}

export const PROPERTY_STAGE_OUTLOOK_FALLBACK: StageOutlook = {
  headline: 'In progress',
  detail: 'We’re working through this property. Its status updates here as it advances.',
  icon: 'clock',
  tone: 'primary',
}

export function propertyStageOutlookOf(stage: string): StageOutlook {
  return PROPERTY_STAGE_OUTLOOK[stage as PropertyStage] ?? PROPERTY_STAGE_OUTLOOK_FALLBACK
}

// ── Media ──────────────────────────────────────────────────────────────────

// PropertyMediaType on the wire. Typed constants rather than loose literals so a
// backend rename breaks the type-check instead of silently rendering every video
// as a broken image.
export type PropertyMediaType = 'image' | 'video'

export const MEDIA_TYPE = {
  image: 'image',
  video: 'video',
} as const satisfies Record<string, PropertyMediaType>

// Human labels for property types (PropertyType).
export const PROPERTY_TYPE_LABELS: Record<string, string> = {
  residential: 'Residential',
  commercial: 'Commercial',
  apartment: 'Apartment',
  villa: 'Villa',
  building: 'Building',
  store: 'Store',
  workshop: 'Workshop',
  land: 'Land',
}

export function propertyTypeLabelOf(type: string): string {
  return PROPERTY_TYPE_LABELS[type] ?? type.replace(/^./, (char) => char.toUpperCase())
}

// Human labels for document types (PropertyDocumentType). Fallback prettifies
// any unknown snake_case type rather than showing the raw enum.
export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  title_deed: 'Title deed',
  settlement_statement: 'Settlement statement',
  payout_receipt: 'Payment receipt',
  valuation_report: 'Valuation report',
  brochure: 'Brochure',
  noc: 'NOC',
  makani: 'Makani',
  rent_agreement: 'Ejari',
  ownership_doc: 'Ownership document',
  white_paper: 'White paper',
  investment_memorandum: 'Investment memorandum',
  proforma: 'Proforma',
  other: 'Other',
}

export function documentTypeLabelOf(type: string): string {
  return (
    DOCUMENT_TYPE_LABELS[type] ??
    type.replace(/_/g, ' ').replace(/^./, (char) => char.toUpperCase())
  )
}

// Document review status → chip (PropertyContentStatus).
export const DOCUMENT_STATUS_META: Record<string, StageMeta> = {
  pending_review: { label: 'Pending review', color: 'warning' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'danger' },
}

export function documentStatusMetaOf(status: string): StageMeta {
  return DOCUMENT_STATUS_META[status] ?? PROPERTY_STAGE_META_FALLBACK
}

// Page size for the paginated list (useInfiniteQuery).
export const PROPERTIES_PAGE_LIMIT = 10
